import * as React from 'react';

import { resolveQuantity } from '@acceligentllc/shared/utils/compoundUnit';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ColorHex } from '@acceligentllc/shared/enums/color';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

const safeParse = <T,>(value: unknown): T | null => {
	if (typeof value !== 'string') return value as T;

	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

type Renderer = (
	value: Nullable<string>,
	timeZone?: Nullable<string>
) => React.ReactNode;

const renderEquipment: Renderer = (value) => {
	const parsed = safeParse<{ id: string; code: string; specification: string; color: string; }>(value);

	if (!parsed?.id) return 'N/A';

	return (
		<>
			<span
				className="condensed-table__employee-list-equipment-row__equipment-color"
				style={{ backgroundColor: ColorHex[parsed.color] }}
			/>
			{parsed.code ?? ''}
			{!!parsed.specification && (
				<span className="condensed-table__employee-list-equipment-row__specification">
					({parsed.specification})
				</span>
			)}
		</>
	);
};

const renderAddress: Renderer = (value) => {
	const parsed = safeParse<{ street: string; }>(value);
	return parsed?.street ?? 'N/A';
};

const renderBoolean: Renderer = (value) =>
	value === 'true' ? 'Yes' : 'No';

const renderSignature: Renderer = (value, timeZone) => {
	const parsed = safeParse<{ id: string; name: string; signedAt: string; }>(value);

	if (!parsed) {
		return 'N/A';
	}

	const timeStamp = `${TimeUtils.formatDate(parsed.signedAt, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME, timeZone ?? undefined)}, ${TimeUtils.abbreviateTimeZone(timeZone ?? null, 'z (Z)')}`;

	return `${parsed.name} ${timeStamp}`;
};

const renderImage: Renderer = (value) => {
	if (value) {
		const fileNameSplit = value.split('/');
		const fileName = fileNameSplit.at(-1);

		return (
			<>
				<a className="link" href={value} rel="noreferrer" target="_blank">
					{fileName}
				</a>
			</>
		);
	}
};

const FIELD_CUSTOM_RENDERERS: Partial<Record<ReportBlockFieldEnum.Type, Renderer>> = {
	[ReportBlockFieldEnum.Type.BOOLEAN]: renderBoolean,
	[ReportBlockFieldEnum.Type.ADDRESS]: renderAddress,
	[ReportBlockFieldEnum.Type.EQUIPMENT]: renderEquipment,
	[ReportBlockFieldEnum.Type.SIGNATURE]: renderSignature,
	[ReportBlockFieldEnum.Type.IMAGE]: renderImage,
};

export const resolveFieldValue = (
	value: Nullable<string>,
	type: Nullable<ReportBlockFieldEnum.Type>,
	unit: Nullable<QuantityUnitType>,
	timeZone?: Nullable<string>
): React.ReactNode => {

	if (!value) return 'N/A';

	const resolver = type && FIELD_CUSTOM_RENDERERS[type];

	if (resolver) {
		return resolver(value, timeZone);
	}

	if (unit && typeof value === 'string') {
		return resolveQuantity(+value, unit);
	}

	return value as React.ReactNode;
};
